import React, { useRef, useState, useEffect } from 'react';
import {
  Row,
  Col,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { snakeCase } from 'lodash';
import { Field, Button, useFeatures } from '@cordata/buffet';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';

import patientService from '~/utils/patient/service';

const otherContactTypeAnswers = [
  'Spouse',
  'Parent',
  'Grandparent',
  'Sibling',
  'Extended Family',
  'Friend',
  'Neighbor',
  'Attorney',
  'Child',
  'Other',
].map(label => ({
  label,
  value: snakeCase(label),
}));

const telephoneTypes = [
  'Home',
  'Mobile',
  'Work',
].map(label => ({
  label,
  value: snakeCase(label),
}));

const HumanInteraction = ({
  data,
  name,
  gender: [
    genderStatus,
    genders,
  ],
}) => {
  const { hasAppCommunity } = useFeatures();
  const [isBirthDateRequired, setIsBirthDateRequired] = useState(!hasAppCommunity);
  const dupCheckId = useRef(null);
  const [matchedPatients, setMatchedPatients] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    // because useFeatures doesn't return instantly
    setIsBirthDateRequired(!hasAppCommunity);
  }, [hasAppCommunity]);

  const toggleModal = () =>
    setIsModalOpen(!isModalOpen);

  useEffect(() => {
    const {
      patient,
      firstName,
      lastName,
      gender,
      birthDate,
    } = data;

    if (!patient &&
        firstName?.length > 2 &&
        lastName?.length > 2 &&
        gender &&
        (isBirthDateRequired ? birthDate : true)
    ) {
      toast.dismiss(dupCheckId.current);

      dupCheckId.current = toast('Running duplication check ...', { autoClose: false });

      patientService
        .dupCheck({
          firstName, lastName, gender, birthDate,
        })
        .then(([, r]) => {
          if (r.length) {
            toast.update(dupCheckId.current, { type: toast.TYPE.SUCCESS, autoClose: 1500, render: 'Duplicates found!' });

            setMatchedPatients(r);
            setIsModalOpen(true);
          } else {
            toast.update(dupCheckId.current, { type: toast.TYPE.INFO, autoClose: 1500, render: 'No duplicates found!' });
          }
        });
    }
  }, [
    data.patient,
    data.firstName,
    data.lastName,
    data.gender,
    data.birthDate,
  ]);

  return (
    <>
      <Field
        name={`${name}.id`}
        label="Id"
        type="hidden"
      />

      {(data.patient && !matchedPatients?.length) && <Alert className="info">Client Found! <Link to={`/patients/${data.patient}/details`} className="alert-link">Go to client.</Link></Alert>}

      <Field
        name={`${name}.otherContactType`}
        label="Other contact type"
        placeholder="Enter other contact type"
        options={otherContactTypeAnswers}
        type="radio-group"
        inline
      />

      <Row form>
        <Col sm>
          <Field
            name={`${name}.firstName`}
            label="First name"
            placeholder="Enter first name"
          />
        </Col>

        <Col sm>
          <Field
            name={`${name}.lastName`}
            label="Last name"
            placeholder="Enter last name"
          />
        </Col>

        <Col sm>
          <Field
            name={`${name}.fullName`}
            label="Street / common name"
            placeholder="Enter street / common name"
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`${name}.telephoneType`}
            label="Telephone type"
            placeholder="Enter telephone type"
            options={telephoneTypes}
            type="radio-group"
            inline
          />
        </Col>

        <Col sm>
          <Field
            name={`${name}.telephoneNumber`}
            label="Telephone number"
            placeholder="Enter telephone number"
            type="tel"
          />
        </Col>
      </Row>

      {(!data.patient || matchedPatients?.length) && <Field
        name={`${name}.createPatient`}
        label="Create a client"
        placeholder="Yes, create a client"
        type="checkbox"
        disabled={data.patient}
      />}

      {(data.patient || data.createPatient) && (
        <Row form>
          <Col sm>
            <Field
              name={`${name}.email`}
              label="Email"
              placeholder="Enter email"
              type="email"
            />
          </Col>

          <Col sm>
            <Field
              name={`${name}.birthDate`}
              label="Birth date"
              placeholder="Enter birth date"
              type="date"
            />
          </Col>

          <Col sm>
            <Field
              name={`${name}.gender`}
              label="Gender"
              options={genders
                ? genders.map(g =>
                  ({ value: g.genderUid, label: g.genderLabel }))
                : []}
              loading={genderStatus === 'loading'}
              type="radio-group"
              inline
            />
          </Col>
        </Row>
      )}

      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Duplicate Search Results</ModalHeader>

        <ModalBody>
          Select one of the results below.
        </ModalBody>

        <ListGroup flush>
          {matchedPatients?.length ? matchedPatients.map(d => (
            <ListGroupItem
              key={d.id}
              action
              className="d-flex justify-content-between align-items-center"
              onClick={() => {
                setFieldValue(`${name}.patient`, d.id);
                toggleModal();
              }}
            >
              {d.lastName}, {d.firstName}

              <FontAwesomeIcon icon="chevron-right" className="text-muted" />
            </ListGroupItem>
          )) : (
            <ListGroupItem>
              No Clients
            </ListGroupItem>
          )}
        </ListGroup>

        <ModalFooter>
          <div className="ml-auto">
            <Button type="cancel" color="tertiary" outline onClick={toggleModal}>Cancel Selection</Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default HumanInteraction;
